import type { BigIntLike } from '../types/index'

/**
 * Coerces a bigint-ish value to a real `bigint`.
 *
 * GraphQL/JSON APIs (notably the Morpho API) serialize `BigInt` scalars as JSON
 * numbers when they fit in a double and as strings otherwise — so a field typed
 * `bigint` by codegen can arrive as `number | string` at runtime. Formatters
 * such as `formatTokenBalance` only apply decimal scaling to genuine bigints, so
 * passing a raw number renders the value ~10^decimals too large. Run untrusted
 * bigint-ish values through this first.
 *
 * Returns `undefined` for nullish or unparseable input. Numbers are truncated to
 * an integer (on-chain amounts are integers; any fractional part is noise).
 * Strings must be plain decimal integers — bare `BigInt(...)` would accept `''`
 * and whitespace (both become `0n`) plus hex/octal/binary prefixes, letting a
 * malformed upstream value render as a real amount instead of falling back.
 */
export function parseBigInt(value: BigIntLike | null | undefined): bigint | undefined {
  if (value == null) return undefined
  if (typeof value === 'bigint') return value
  if (typeof value === 'number') {
    return Number.isFinite(value) ? BigInt(Math.trunc(value)) : undefined
  }
  return /^-?\d+$/.test(value) ? BigInt(value) : undefined
}
